<template>
<div class="container-fluid">

	<page-loading v-if="!data_loaded" />

	<template v-else>
		<div class="row">
		    <div class="col-12">
				<h1 data-test="pageTitle">Edit User: {{ edit_user.display_name }}</h1>
			</div>
		</div>

		<form @submit.prevent="submitForm" @keydown="form.onKeydown($event)" @change="form.onKeydown($event)" class="mb-0">
			<alert-errors :form="form">There were some problems with your input.</alert-errors>

			<div class="row">
				<div class="col">
					<div class="card">
						<div class="d-flex" style="min-height:400px">

							<div class="card-side-menu bg-gray-100 rounded-left" style="min-width:150px;">
								<div class="card-header bg-gray-600 text-white" style="border-top-right-radius: 0px; border-bottom:1px solid #6c757d;">Settings</div>
								<div class="flex-column p-2">
									<span class="btn-block btn btn-ghost-primary text-left px-25 leading-tight" :class="{'active' : active_panel == 'profile'}" @click="setPanel('profile')" data-test="selectProfileTab">Profile</span>
									<span class="btn-block btn btn-ghost-primary text-left px-25 leading-tight" :class="{'active' : active_panel == 'access'}" @click="setPanel('access')" data-test="selectAccessTab">Access</span>
									<span class="btn-block btn btn-ghost-primary text-left px-25 leading-tight" :class="{'active' : active_panel == 'organization'}" @click="setPanel('organization')" data-test="selectOrganizationTab">Organization</span>
									<span class="btn-block btn btn-ghost-primary text-left px-25 leading-tight" :class="{'active' : active_panel == 'clients'}" @click="setPanel('clients')" data-test="selectClientsTab">Clients</span>
									<span v-if="perms.can_do_everything" class="btn-block btn btn-ghost-primary text-left px-25 leading-tight" :class="{'active' : active_panel == 'subscriptions'}" @click="setPanel('subscriptions')" data-test="selectSubscriptionsTab">Subscriptions</span>
									<span class="btn-block btn btn-ghost-primary text-left px-25 leading-tight" :class="{'active' : active_panel == 'locations'}" @click="setPanel('locations')" data-test="selectLocationsTab">Locations</span>

								</div>
							</div>


							<div v-show="active_panel == 'profile'" :class="{'pending' : pending }" class="content-panel flex-grow-1">
								<div class="card-header">Profile</div>
								<div class="card-body row">

									<div v-if="edit_user.status != 1" class="col-12">
										<div class="rounded bg-red-100 text-red-700 text-sm p-25 mb-35 leading-tight font-weight-bold">
											This account is disabled.
										</div>
									</div>

									<div class="col-md-auto" style="max-width:200px;">
										<file-field
											ref="image"
											:default_url="edit_user.photo_url"
											@file_changed="changedImage($event)"
										/>
								    </div>

									<div class="col">	
									    <div class="form-group">
											<label class="mb-0">First Name</label>
											<input class="form-control" v-model="form.first_name" required data-test="userFirstName">
									    </div>

									    <div class="form-group">
											<label class="mb-0">Last Name</label>
											<input class="form-control" v-model="form.last_name" required data-test="userLastName">
									    </div>
									    
									    <div class="form-group">
										    <label class="mb-0">Email</label>
										    <input class="form-control" v-model="form.email" type="email" autocomplete="off" data-lpignore="true" :disabled="!perms.can_do_everything" data-test="userEmail">
										    <div class="form-text text-sm text-gray-500 leading-tight">Contact your account manager to change email</div>
									    </div>							    
									</div>
									<div class="col-12 text-right">
										<button type="submit" class="btn btn-primary" data-test="saveUserProfileButton">Save Profile</button>
									</div>
								</div>						
							</div>

							<div v-show="active_panel == 'access'" :class="{'pending' : pending }" class="content-panel flex-grow-1">
								<div class="card-header">Access</div>
								<div class="card-body">
									<div class="form-group">
										<div class="mb-0 text-blue-700 font-weight-bold">User Status</div>
										<v-select        
				                            :options="[
				                            	{ label:'Active', value:1 },
				                            	{ label:'Disabled', value:0 }
				                            ]"
				                            :reduce="label => label.value"
				                            v-model="form.status"
				                            :clearable="false"				                            
											data-test="userStatus"
				                        />

										<div class="mt-35 text-right">
											<button type="submit" class="btn btn-primary" data-test="saveUserStatusButton">Save Status</button>
										</div>	

				                        <div class="mt-35 mb-3" style="border-top:1px solid #e4e7ea;margin-left:-20px; margin-right:-20px;"></div>
									</div>	

									<!-- User admins can send reset email -->
									<div class="text-blue-700 font-weight-bold pb-1">Send Password Reset Email</div>
									<span class="btn btn-sm btn-light" @click="sendPasswordReset()" data-test="resetPasswordButton">Reset Password</span>

									<!-- Only sitewide admins can edit passwords -->
									<template v-if="perms.can_do_everything">
							    		<div class="mt-35 mb-3" style="border-top:1px solid #e4e7ea;margin-left:-20px; margin-right:-20px;"></div>
										<div class="text-blue-700 font-weight-bold pb-1">Change Password</div>
										<div class="form-group">
											<label class="mb-0">New Password</label>
											<input class="form-control w-auto" v-model="pw_form.password" type="password" autocomplete="new-password" data-lpignore="true" data-test="userPassword">
											<password-reqs
												v-if="edit_user.password_reqs"					
												:password="pw_form.password"
												:requirements="edit_user.password_reqs" 
												@pw_checks_changed="changedPwChecks($event)" />
									    </div>	
										<div class="form-group pb-1">
											<label class="mb-0">Confirm</label>
											<input class="form-control w-auto" v-model="pw_form.password_confirmation" type="password" autocomplete="new-password" data-lpignore="true" data-test="userPasswordConfirmation">
											<div class="form-text text-base text-gray-600 mb-0">
												<i :class="pw_form.password != null && pw_form.password == pw_form.password_confirmation ? 'fal fa-check text-green-500' : 'fal fa-long-arrow-right'"></i>
												Passwords match
											</div>											
									    </div>

										<div class="text-right">
											<span class="btn btn-primary"
												:class="{ 'disabled' : disablePasswordSubmit }"
												@click="disablePasswordSubmit ? '' : submitPassword()"
												data-test="saveUserPasswordButton">Save Password
											</span>
										</div>										
									</template>								
								</div>
							</div>

							<div v-show="active_panel == 'organization'" :class="{'pending' : pending }" class="content-panel flex-grow-1">
								<div class="card-header">Organization</div>
								<div v-if="perms.can_assign_user_org" class="card-body">

									<div class="rounded bg-yellow-100 text-yellow-700 text-sm p-25 mb-25 leading-tight font-weight-bold">
										Organizations allow management and viewing of multiple clients. Organization admins can manage org users and have admin-level access to all clients and users in their organization.
									</div>

								    <div class="form-group">
									    <div class="text-blue-700 font-weight-bold pb-1">Organization</div>
									    <div v-if="!all_orgs.length" class="text-gray-500"><em>No organizations found</em></div>

									    <v-select
									    	v-else        
				                            :options="all_orgs"
				                            label="name"
				                            :reduce="name => name.id"
				                            v-model="selected_org"
				                            :clearable="true"
				                            placeholder="Assign to organization - Optional"
				                        />

				                        <div v-if="perms.can_assign_user_role && selected_org" class="form-group form-check mt-2">
				                        	<input type="checkbox" class="form-check-input" v-model="set_org_admin">
											<label class="form-check-label">Set as organization admin</label>
				                        </div>
									</div>



									<div class="text-right">
										<button type="submit" class="btn btn-primary">Save Org Settings</button>
									</div>
								</div>


								<div v-else class="card-body">
									<div class="rounded bg-yellow-100 text-yellow-700 text-sm p-25 mb-25 leading-tight">
										Organizations allow management and viewing of multiple clients.
									</div>
									<div class="text-blue-700 font-weight-bold pb-1">Organization</div>
									<div v-if="edit_user.org_id" class="form-check">
										<input type="checkbox" class="form-check-input" checked disabled>
										<label class="">{{ edit_user.org.name }}</label>
									</div>
									<div v-else class="text-gray-500">
										<em>Not assigned</em>
									</div>
								</div>
							</div>

							<div v-if="perms.can_assign_user_client" v-show="active_panel == 'clients'" :class="{'pending' : pending }" class="content-panel flex-grow-1">
								<div class="card-header">Clients</div>
								<div class="card-body row">
									<div v-if="userIsOrgAdmin" class="col-12">
										<div class="rounded bg-yellow-100 text-yellow-700 text-sm p-25 mb-25 font-weight-bold leading-tight">
									    	Org admins have admin-level access to all clients in the organization. Users are not visible to a client unless explicitly assigned.
									    </div>
										<ul class="pl-35 text-gray-700">
											<li v-for="client in edit_user.clients_through_org">
												{{ client.name }}
											</li>
										</ul>

										<div class="mt-35 mb-3" style="border-top:1px solid #e4e7ea;margin-left:-20px; margin-right:-20px;"></div>
									</div>

									<template>
									    <div class="col" data-test="userClients">
										    <div class="text-blue-700 font-weight-bold pb-1">Assigned Clients:</div>
										    <div v-for="client in userIsOrgAdmin ? edit_user.clients_through_org : all_clients" class="form-check">
										    	<input 
										    		type="checkbox" class="form-check-input"
										    		:value="client.id" v-model="form.client_list"
										    		:data-test="form.client_list.includes(client.id)">
										    	<label class="form-check-label">{{ client.name }}</label>
											</div>
										</div>

									 	<div v-if="perms.can_assign_user_role" class="col" data-test="userRoles">
											<label class="mb-0 text-blue-700 font-weight-bold">Client Roles - <span class="text-gray-500">Assign roles carefully!</span></label>

											<div v-for="role in all_roles.filter(role => role.name != 'org admin' && role.name != 'org member')" class="form-check">
												<input type="checkbox" class="form-check-input" :value="role.id" v-model="selected_roles">
												<label class="form-check-label">{{ role.name }}</label>
											</div>

										    <div class="form-check my-2 pt-2 border-top">
										    	<input type="checkbox" class="form-check-input" v-model="form.clear_user_roles">
										    	<label class="form-check-label font-weight-bold">Remove all roles</label> <span v-if="selected_org">(including org role)</span>
										    </div>					    
									    </div>

										<div class="col-12 text-right">
											<button type="submit" class="btn btn-primary" data-test="saveUserClientsButton">Save Settings</button>
										</div>										    
									</template>
								</div>					
							</div>

							<div v-if="perms.can_do_everything" v-show="active_panel == 'subscriptions'" :class="{'pending' : pending }" class="content-panel flex-grow-1" data-test="userSubscriptions">
								<div class="card-header">Subscriptions</div>
								<div class="card-body">
									<div class="text-blue-700 font-weight-bold pb-1">Client Snapshot<br><span class="text-gray-500 font-weight-normal">A glance of activity at all of your facilties in the past week. Delivered Monday morning.</span></div>
									<div class="form-check">
										<input type="checkbox" class="form-check-input" v-model="client_snapshot">
										<label class="form-check-label">Subscribed</label>
									</div>

									<div class="mt-3 mb-3" style="border-top:1px solid #e4e7ea;margin-left:-20px; margin-right:-20px;"></div>

								    <div class="text-blue-700 font-weight-bold pb-1">Weekly Digest<br><span class="text-gray-500 font-weight-normal">An overview of team check-ins for the prior week. Delivered Monday morning.</span></div>								    

								    <div v-for="client in edit_user.client_list_all" class="form-check">
										<input type="checkbox" class="form-check-input" :value="client.id" v-model="subscriptions.weekly_digest">
								    	<label class="form-check-label">{{ client.name }}</label>
								    </div>
									<div class="text-right">
										<span class="btn btn-primary" @click="submitSubscriptions()" data-test="saveUserSubscriptionsButton">Save Subscriptions</span>
									</div>
								</div>
							</div>

							<div v-show="active_panel == 'locations'" class="flex-grow-1 user-locations" data-test="userLocations">
								<locations-manager
					            	:can_update_locations="true"
					                :locationable_id="edit_user.id"
					                locationable_type="User"
					                :locations="edit_user.locations"
					            >
					            	<template v-slot:alert>
										<div class="rounded bg-yellow-100 text-yellow-700 text-sm p-25 mx-35 mt-35 font-weight-bold leading-tight">
											Edit other user's locations with caution!
										</div>
					            	</template>
					            </locations-manager>								
							</div>

						</div>
					</div>
				</div>
			</div>


			<div class="row">
				<div class="col-12">		            
				    <div class="form-group">
				    	<span class="btn btn-ghost-secondary mr-1" @click="cancelEdit()">Cancel</span>				    	
					</div>
				</div>
			</div>
		</form>


	</template>

</div>
</template>

<script>
	//USER EDITING SOMEONE ELSE

	import { mapState } from 'vuex';
    import BreadcrumbsManager from '../../helpers/BreadcrumbManager';
	import { Form, HasError, AlertError, AlertErrors, AlertSuccess } from 'vform';
	import { objectToFormData } from 'object-to-formdata';
	import LocationsManager from '../../components/LocationsManager';
	import PasswordReqs from '../../components/PasswordReqs';

	export default {
		mixins: [BreadcrumbsManager],

    	components: {
            HasError,
            AlertError,
            AlertErrors,
            AlertSuccess,
            Form,
            LocationsManager,
            PasswordReqs
    	},

		props: {
			id: {
				type: Number,
				required: true,
			},
		},

		data() {
			return {
				active_panel: 'profile',
				all_clients: null,
				all_orgs: null,
                edit_user: null,
                data_loaded: false,
                pending: false,
                form: {},
                pw_form: {},
                pw_checks: {},
                all_roles: null,

                selected_org: null,
                set_org_admin: null,
                selected_roles: null,

                default_client_list: [],

                client_snapshot: null,
                subscriptions: {
                	weekly_digest: [],
                	client_snapshot: [],
                },                
			}
		},

		created() {
			this.fetchClientIds();
			this.fetchOrgIds();
			this.fetchRoles();
			if (perms.can_do_everything) {
				this.fetchSubscriptions();
			}		
		},

        activated() {        	
            this.fetchUser();
            this.setBreadcrumbs([
                { text: 'Home', to: { name: 'home' } },
                { text: 'Users', to: { name: 'users.index'} },
                { text: '[User Name]', to: { name: 'accounts.show'} },
            ]);
        },

		methods: {
			cancelEdit() {
				this.$router.go(-1);
			},

			changedImage($event) {
                this.form.image = this.$refs.image.file;
            },

            changedPwChecks($event) {
            	this.pw_checks = $event;
            },

            fetchClientIds() {
            	if (this.perms.can_assign_user_client) {
					axios.get('/api/admin/clients/ids').then(({data}) => {
						this.all_clients = data;
					});
				}
            },

            fetchOrgIds() {
            	if (this.perms.can_assign_user_org) {
					axios.get('/api/admin/orgs/ids').then(({data}) => {
						this.all_orgs = data;
					});
				}
            },

            fetchRoles() {
            	if (this.perms.can_assign_user_role) {
					axios.get('/api/roles/available').then(({data}) => {
						this.all_roles = data;
					});
				}
            },

            fetchSubscriptions() {
            	axios.get('/api/subscriptions/user/' + this.$route.params.id).then(({data}) => {
            		this.setSubscriptions(data);
	        	});
            },

            fetchUser() {
            	axios.get('/api/admin/users/' + this.$route.params.id).then(({data}) => {
            		this.setUserData(data);
                    this.replaceBreadcrumb({
                        find: '[User Name]',
                        replace: { text: this.edit_user.display_name }
                    });
					this.data_loaded = true; 
	        	});
            },

            fillForm(userData) {
				this.form = new Form({
					first_name: userData.first_name,
					last_name: userData.last_name,
					email: userData.email,
					status: userData.status,
					image: userData.image,

					//do the logic on the front end, the back just processes
					user_roles: this.combined_user_roles,
					clear_user_roles: null,
				});

				if (this.perms.can_assign_user_client) {
					this.form.client_list = userData.client_list_all.map(client => client.id);
				}
				if (this.perms.can_assign_user_org) {
					this.form.org_id = this.selected_org; //need a copy
				}

				if (this.perms.can_do_everything) {
					this.pw_form = new Form({
						password: null,
						password_confirmation: null,
					});
				}
            },

            sendPasswordReset() {
				Swal.fire({
					title: 'Are you sure?',
					icon: 'warning',
					showCancelButton: true,
					cancelButtonText: 'Cancel',
					confirmButtonText: 'Send it!',
					animation: false,
				}).then((result) => {
					if (result.value) {
		            	axios.post('/api/admin/users/send_password_reset_link/' + this.edit_user.id).then(({ data }) => {
							Swal.fire({
								toast: true,
								position: 'top-end',
								showConfirmButton: false,
								timer: 4000,
								icon: 'success',
								title: 'Password reset sent',
							});
		            	});
        			}
    			});
            },

            setPanel(value) {
            	this.active_panel = value;
            },

            setSubscriptions(data) {
            	this.client_snapshot = data.filter(sub => sub.subscribed_to == 'client_snapshot' && sub.status == 1).length;
            	this.subscriptions.weekly_digest = data.filter(sub => sub.subscribed_to == 'weekly_digest' && sub.status == 1).map(sub => sub.client_id);
            },

            setUserData(data) {            		
				this.edit_user = data;
				this.selected_org = data.org_id;
				this.selected_roles = data.roles.filter(role => role.name != 'org admin' && role.name != 'org member').map(role => role.id);
				this.set_org_admin = this.userIsOrgAdmin;
				this.fillForm(data);                      
            },

        	submitForm() { 
            	this.pending = true;
                this.form.submit('post', '/api/admin/users/' + this.$route.params.id, {
					transformRequest: [function (data, headers) {
						data['_method'] = 'PUT';
						return objectToFormData(data)
					}],
                }).then(({ data }) => {
					Swal.fire({
						toast: true,
						position: 'top-end',
						showConfirmButton: false,
						timer: 4000,
						icon: 'success',
						title: 'User saved',
					});	
					this.setUserData(data);
					this.pending = false;
            	}).catch((error) => { 
            		this.pending = false;
            	});
        	},

        	submitPassword() {
            	this.pending = true;

                this.pw_form.post('/api/admin/users/' + this.$route.params.id + '/update-password').then(({ data }) => {
					Swal.fire({
						toast: true,
						position: 'top-end',
						showConfirmButton: false,
						timer: 4000,
						icon: 'success',
						title: 'Password changed',
					});
					this.pending = false;
            	}).catch((error) => { 
            		this.pending = false;
            	});
        	},

        	submitSubscriptions() {
        		this.pending = true;
        		axios.post('/api/subscriptions/user/' + this.$route.params.id, { subscriptions: this.subscriptions }).then(({data}) => {
					Swal.fire({
						toast: true,
						position: 'top-end',
						showConfirmButton: false,
						timer: 4000,
						icon: 'success',
						title: 'Subscriptions saved',
					});        			
        			this.setSubscriptions(data);
        			this.pending = false;
        		}).catch((error) => { 
            		this.pending = false;
            	});
            },  	
		},

		computed: {
			...mapState(['clients', 'perms', 'user']),			
			
			combined_user_roles() {				
				//if org is selected, check if admin, otherwise set as member
				if (this.selected_org) {
					let combined_roles = [...this.selected_roles];
					if (this.set_org_admin) {
						combined_roles.push(this.all_roles.find(role => role.name == 'org admin').id);
					}
					else {
						combined_roles.push(this.all_roles.find(role => role.name == 'org member').id);
					}
					return combined_roles;
				}
				//easier than trying to splice
				else {
					return this.selected_roles;
				}
				
			},

			disablePasswordSubmit() {
				if (Object.values(this.pw_checks).includes(false) || !this.pw_form.password || this.pw_form.password != this.pw_form.password_confirmation) {
					return true;
				}
				return false;
			},

			userIsOrgAdmin() {
				return this.edit_user.roles.some(role => role.name == 'org admin')
			}			
		},

		watch: {
			client_snapshot() {
				if (this.client_snapshot == true) {
					this.subscriptions.client_snapshot = [0];
				}
				else {
					this.subscriptions.client_snapshot.length = 0;
				}
			},
						
			combined_user_roles() {
				this.form.user_roles = this.combined_user_roles;
			},
			
			selected_org() {
				this.form.org_id = this.selected_org;
			}			
		}

	}
</script>

<style>
	.user-locations .card {
		box-shadow: none;
	}
</style>